import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isFirebaseConfigured } from "@/lib/firebaseConfig";
import {
  confirmFirebaseOtp,
  firebasePhoneErrorMessage,
  resetFirebaseRecaptcha,
  sendFirebaseOtp,
} from "@/lib/firebasePhoneAuth";
import { loadSavedPartnerPhone } from "@/lib/driverPartner";
import { loginTsFirebase, isRiderUseDaError, isTsNeedsSignupError, markTsFreshLogin } from "@/lib/tsIdentity";
import { deliveryJoinPath, staffBrandKicker, staffBrandTitle } from "@/lib/teamHub";

const PINK = "#EE2677";
const RESEND_SECONDS = 60;

/** Staff SMS OTP login only — session gate handles onboarding after OTP. */
export default function TsAuthGate({ onComplete }) {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState(() => loadSavedPartnerPhone());
  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [riderBlocked, setRiderBlocked] = useState(false);
  const [info, setInfo] = useState("");
  const [resendIn, setResendIn] = useState(0);

  const cleanPhone = phone.replace(/\D/g, "").slice(-10);
  const maskedPhone =
    cleanPhone.length === 10 ? `+91 ${cleanPhone.slice(0, 5)} ${cleanPhone.slice(5)}` : "";

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  useEffect(() => () => resetFirebaseRecaptcha(), []);

  const resetPhoneStep = () => {
    resetFirebaseRecaptcha();
    setConfirmationResult(null);
    setStep("phone");
    setOtp("");
    setErr("");
    setRiderBlocked(false);
    setInfo("");
    setResendIn(0);
  };

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (cleanPhone.length !== 10) {
      setErr("Enter your 10-digit mobile number");
      return;
    }
    if (!isFirebaseConfigured()) {
      setErr("SMS login is not configured yet. Ask your manager.");
      return;
    }
    setBusy(true);
    setErr("");
    setRiderBlocked(false);
    setInfo("");
    try {
      const result = await sendFirebaseOtp(cleanPhone);
      setConfirmationResult(result);
      setStep("otp");
      setOtp("");
      setResendIn(RESEND_SECONDS);
      setInfo("Check your phone for a 6-digit code via SMS.");
    } catch (ex) {
      setErr(firebasePhoneErrorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e?.preventDefault?.();
    const code = otp.replace(/\D/g, "");
    if (code.length !== 6) {
      setErr("Enter all 6 digits from SMS");
      return;
    }
    if (!confirmationResult) {
      setErr("Tap Send code and try again.");
      return;
    }
    setBusy(true);
    setErr("");
    setRiderBlocked(false);
    try {
      const firebaseIdToken = await confirmFirebaseOtp(confirmationResult, code);
      try {
        const data = await loginTsFirebase(firebaseIdToken);
        if (data?.status === "pending_approval") {
          onComplete?.({ status: "pending_approval", employee: data.employee, firebaseIdToken });
          return;
        }
        markTsFreshLogin();
        onComplete?.({ status: "ready", ...data });
      } catch (loginEx) {
        const loginMsg = loginEx.message || "";
        if (isTsNeedsSignupError(loginMsg)) {
          onComplete?.({ status: "needs_signup", firebaseIdToken, phone: cleanPhone });
          return;
        }
        throw loginEx;
      }
    } catch (ex) {
      const message = ex.message || firebasePhoneErrorMessage(ex);
      if (isRiderUseDaError(message)) {
        setRiderBlocked(true);
        setErr("");
      } else {
        setRiderBlocked(false);
        setErr(message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tb-auth">
      <style>{`
        .tb-auth {
          min-height: 100dvh;
          background: #F5F2EA;
          color: #2D2D2D;
          padding: 12px 14px 28px;
          max-width: 480px;
          margin: 0 auto;
          font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
        }
        .tb-auth * { box-sizing: border-box; }
        .ts-auth-brand { text-align: center; margin: 24px 0 28px; }
        .ts-auth-brand-kicker {
          font-size: 11px; font-weight: 800; letter-spacing: 0.12em;
          text-transform: uppercase; color: rgba(45,45,45,0.45); margin: 0 0 4px;
        }
        .ts-auth-brand-name { font-size: 28px; font-weight: 900; color: ${PINK}; margin: 0; }
        .ts-auth-card {
          background: #fff; border-radius: 20px; padding: 20px 18px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }
        .ts-auth-title { font-size: 20px; font-weight: 800; margin: 0 0 6px; }
        .ts-auth-sub { font-size: 14px; color: rgba(45,45,45,0.65); margin: 0 0 16px; line-height: 1.45; }
        .ts-auth-info {
          margin-bottom: 12px; background: #FFF0F6; border: 1px solid #F9A8D4;
          border-radius: 12px; padding: 10px 12px; font-size: 13px; font-weight: 600;
          color: #9D174D; text-align: center;
        }
        .ts-auth-field label { display: block; font-size: 13px; font-weight: 700; margin-bottom: 6px; }
        .ts-auth-field input {
          width: 100%; border: 2px solid #E5E7EB; border-radius: 14px;
          padding: 14px; font-size: 18px; background: #fff;
        }
        .ts-auth-otp { letter-spacing: 0.35em; text-align: center; font-weight: 800; }
        .ts-auth-primary {
          width: 100%; border: none; border-radius: 16px; padding: 16px;
          font-size: 17px; font-weight: 800; color: #fff; background: ${PINK};
          cursor: pointer; margin-top: 14px;
        }
        .ts-auth-primary:disabled { opacity: 0.6; cursor: wait; }
        .ts-auth-link {
          width: 100%; margin-top: 12px; border: none; background: transparent;
          color: ${PINK}; font-size: 15px; font-weight: 800; cursor: pointer; padding: 10px;
        }
        .ts-auth-err { color: #B91C1C; font-size: 14px; font-weight: 600; margin-top: 10px; }
        .ts-auth-resend {
          width: 100%; margin-top: 10px; border: none; background: transparent;
          color: ${PINK}; font-size: 14px; font-weight: 800; cursor: pointer; padding: 8px;
        }
        .ts-auth-resend:disabled { color: rgba(45,45,45,0.4); cursor: default; }
        .ts-auth-rider-note {
          margin: 0 0 16px; padding: 12px 14px; border-radius: 14px;
          background: #EFF6FF; border: 1px solid #BFDBFE; font-size: 13px;
          font-weight: 600; color: #1E40AF; line-height: 1.45;
        }
        .ts-auth-rider-note a { color: #1D4ED8; font-weight: 800; }
        .ts-auth-rider-block {
          margin-top: 14px; padding: 14px; border-radius: 14px;
          background: #FFF7ED; border: 1px solid #FDBA74; text-align: center;
        }
        .ts-auth-rider-block p {
          margin: 0 0 12px; font-size: 14px; font-weight: 700; color: #9A3412; line-height: 1.45;
        }
        .ts-auth-da-link {
          display: inline-block; padding: 12px 18px; border-radius: 14px;
          background: ${PINK}; color: #fff; font-size: 15px; font-weight: 800;
          text-decoration: none;
        }
      `}</style>

      <p className="ts-auth-rider-note">
        Delivery riders: use the <strong>Delivery app</strong>, not staff login —{" "}
        <Link to={deliveryJoinPath()}>open Delivery join</Link>
      </p>

      <div className="ts-auth-brand">
        <p className="ts-auth-brand-kicker">{staffBrandKicker()}</p>
        <h1 className="ts-auth-brand-name">{staffBrandTitle()}</h1>
      </div>

      <div className="ts-auth-card">
        <h2 className="ts-auth-title">{step === "otp" ? "Verify SMS code" : "Sign in"}</h2>
        <p className="ts-auth-sub">
          {step === "otp"
            ? `Enter the 6-digit code we sent to ${maskedPhone}.`
            : "One login for Kitchen, Delivery, Inventory, and Chat. Verify your mobile with SMS."}
        </p>

        {info && !err ? <p className="ts-auth-info">{info}</p> : null}

        {step === "phone" ? (
          <form onSubmit={sendCode}>
            <div className="ts-auth-field">
              <label htmlFor="ts-login-phone">Mobile number</label>
              <input
                id="ts-login-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="10-digit mobile"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            {riderBlocked ? (
              <div className="ts-auth-rider-block">
                <p>
                  This number is a delivery rider. Staff login is for internal team only.
                  Use the Delivery app to run your route.
                </p>
                <Link to={deliveryJoinPath()} className="ts-auth-da-link">
                  Open Delivery app
                </Link>
              </div>
            ) : null}
            {err ? <p className="ts-auth-err">{err}</p> : null}
            {!riderBlocked ? (
              <button type="submit" className="ts-auth-primary" disabled={busy}>
                {busy ? " Sending code…" : "Send login code"}
              </button>
            ) : null}
          </form>
        ) : (
          <form onSubmit={verifyCode}>
            <div className="ts-auth-field">
              <label htmlFor="ts-login-otp">6-digit SMS code</label>
              <input
                id="ts-login-otp"
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="••••••"
                className="ts-auth-otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
              />
            </div>
            {riderBlocked ? (
              <div className="ts-auth-rider-block">
                <p>
                  This number is a delivery rider. Staff login is for internal team only.
                  Use the Delivery app to run your route.
                </p>
                <Link to={deliveryJoinPath()} className="ts-auth-da-link">
                  Open Delivery app
                </Link>
              </div>
            ) : null}
            {err ? <p className="ts-auth-err">{err}</p> : null}
            {!riderBlocked ? (
              <button
                type="submit"
                className="ts-auth-primary"
                disabled={busy || otp.replace(/\D/g, "").length !== 6}
              >
                {busy ? " Verifying…" : "Verify & continue"}
              </button>
            ) : null}
            {!riderBlocked ? (
              <button
                type="button"
                className="ts-auth-resend"
                disabled={busy || resendIn > 0}
                onClick={sendCode}
              >
                {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
              </button>
            ) : null}
            <button type="button" className="ts-auth-link" onClick={resetPhoneStep}>
              Change phone number
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
